/**
 * `DatePicker` — a calendar icon button (right of the window selector) that
 * opens a month-grid popover for jumping to a specific day's data. Selecting a
 * day sets the uptime `anchor` drill-down via `onPickDate`.
 *
 * Brutal-minimal per DESIGN.md: hairline borders, no gradients, the active day
 * uses the same `bg-ink-1 / text-paper-1` treatment as the window selector's
 * pill. All date math is the pure, tested `lib/calendar.ts`; this is a thin
 * view. UTC throughout (the page states facts in UTC).
 */
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import {
  addMonths,
  buildMonthView,
  formatDayLabel,
  isSelectable,
  selectableRange,
  utcDayKey,
  WEEKDAY_LABELS,
} from '../lib/calendar';

export interface DatePickerProps {
  /** Currently anchored day key (`YYYY-MM-DD`) to highlight, or null. */
  selectedIso: string | null;
  onPickDate: (dayIso: string) => void;
  /** Clears the anchored date (the × inside the active pill). */
  onClear: () => void;
  /** Injectable for determinism; defaults to the real clock. */
  now?: Date;
}

const CalendarIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    <rect x={3} y={4.5} width={18} height={16} rx={2.5} />
    <path d="M3 9h18M8 2.5v4M16 2.5v4" />
  </svg>
);

const Chevron = ({ dir }: { dir: 'left' | 'right' }) => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-3.5 w-3.5"
    aria-hidden
  >
    <path d={dir === 'left' ? 'M10 4L6 8l4 4' : 'M6 4l4 4-4 4'} />
  </svg>
);

export function DatePicker({
  selectedIso,
  onPickDate,
  onClear,
  now,
}: DatePickerProps) {
  const clock = now ?? new Date();
  const range = selectableRange(clock, 90);
  const todayIso = utcDayKey(clock);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => {
    const seed = selectedIso ?? todayIso;
    const d = new Date(`${seed}T00:00:00.000Z`);
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
  });
  const wrapRef = useRef<HTMLDivElement>(null);

  // When reopened, jump the grid to the anchored (or current) month.
  useEffect(() => {
    if (!open) return;
    const seed = selectedIso ?? todayIso;
    const d = new Date(`${seed}T00:00:00.000Z`);
    setView({ year: d.getUTCFullYear(), month: d.getUTCMonth() });
  }, [open, selectedIso, todayIso]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const month = buildMonthView(view.year, view.month);
  const min = new Date(`${range.minIso}T00:00:00.000Z`);
  const max = new Date(`${range.maxIso}T00:00:00.000Z`);
  const canPrev =
    view.year > min.getUTCFullYear() ||
    (view.year === min.getUTCFullYear() && view.month > min.getUTCMonth());
  const canNext =
    view.year < max.getUTCFullYear() ||
    (view.year === max.getUTCFullYear() && view.month < max.getUTCMonth());

  const step = (delta: number) => {
    setView((v) => addMonths(v.year, v.month, delta));
  };

  const anchored = selectedIso !== null;

  const triggerBtn = (
    <button
      type="button"
      onClick={() => {
        setOpen((v) => !v);
      }}
      aria-label="Choose date"
      aria-haspopup="dialog"
      aria-expanded={open}
      className={clsx(
        'flex items-center justify-center rounded px-2 py-1 outline-none transition-colors focus-visible:shadow-ring-blue',
        anchored
          ? 'text-paper-1 hover:bg-paper-1/10' // inside the black active pill
          : open
            ? 'bg-paper-2 text-ink-1'
            : 'text-ink-2 hover:bg-paper-2 hover:text-ink-1',
      )}
    >
      <CalendarIcon className="h-[17px] w-[17px]" />
    </button>
  );

  return (
    <div ref={wrapRef} className="relative">
      {selectedIso !== null ? (
        // One black pill: calendar + date + ×, so the whole active selection
        // reads as filled (not just the icon).
        <div className="flex items-center gap-0.5 rounded bg-ink-1 pr-0.5 text-paper-1">
          {triggerBtn}
          <span className="text-[12px] font-medium [font-feature-settings:'tnum']">
            {formatDayLabel(selectedIso)}
          </span>
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear date"
            className="flex h-6 w-6 items-center justify-center rounded text-paper-1 outline-none transition-colors hover:bg-paper-1/10 focus-visible:shadow-ring-blue"
          >
            <svg
              viewBox="0 0 16 16"
              className="h-3 w-3"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinecap="round"
            >
              <path d="M3 3l10 10M13 3L3 13" />
            </svg>
          </button>
        </div>
      ) : (
        triggerBtn
      )}

      {open && (
        // Non-modal popover, not a dialog: it closes on Escape / outside
        // click and does not trap focus, so `role="dialog"` would promise a
        // modal focus contract we intentionally don't implement here. The
        // trigger's aria-haspopup/aria-expanded already convey the popover
        // relationship. (a11y: interaction-4)
        <div
          aria-label="Choose date"
          className="absolute right-0 z-40 mt-2 w-[268px] rounded-lg border border-line bg-paper-1 p-3 shadow-pop animate-[modal-pop_120ms_ease-out]"
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                step(-1);
              }}
              disabled={!canPrev}
              aria-label="Previous month"
              className="flex h-7 w-7 items-center justify-center rounded text-ink-2 outline-none transition-colors hover:bg-paper-2 hover:text-ink-1 focus-visible:shadow-ring-blue disabled:pointer-events-none disabled:opacity-30"
            >
              <Chevron dir="left" />
            </button>
            <span className="text-[13px] font-semibold text-ink-1">
              {month.label}
            </span>
            <button
              type="button"
              onClick={() => {
                step(1);
              }}
              disabled={!canNext}
              aria-label="Next month"
              className="flex h-7 w-7 items-center justify-center rounded text-ink-2 outline-none transition-colors hover:bg-paper-2 hover:text-ink-1 focus-visible:shadow-ring-blue disabled:pointer-events-none disabled:opacity-30"
            >
              <Chevron dir="right" />
            </button>
          </div>

          <div className="mb-1 grid grid-cols-7 gap-0.5">
            {WEEKDAY_LABELS.map((w) => (
              <div
                key={w}
                className="flex h-6 items-center justify-center text-[10.5px] font-medium text-ink-3"
              >
                {w}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {month.weeks.flat().map((cell) => {
              const selectable = cell.inMonth && isSelectable(cell.iso, range);
              // Only the real in-month cell gets the black "selected" fill —
              // an out-of-month padding copy of the same day stays faint.
              const isSelectedDay = cell.inMonth && cell.iso === selectedIso;
              const isToday = cell.iso === todayIso;
              return (
                <button
                  key={cell.iso}
                  type="button"
                  disabled={!selectable}
                  aria-label={cell.iso}
                  aria-pressed={isSelectedDay}
                  onClick={() => {
                    onPickDate(cell.iso);
                    setOpen(false);
                  }}
                  className={clsx(
                    'flex h-8 w-8 items-center justify-center rounded text-[12px] tabular-nums outline-none transition-colors [font-feature-settings:"tnum"] focus-visible:shadow-ring-blue',
                    // Exactly one branch sets the colour, so a selected day can
                    // never also inherit the faint out-of-month colour
                    // (the black-digit-on-black-fill bug).
                    isSelectedDay
                      ? 'bg-ink-1 font-semibold text-paper-1'
                      : !cell.inMonth
                        ? 'text-ink-3/40'
                        : selectable
                          ? clsx(
                              'text-ink-1 hover:bg-paper-2',
                              isToday && 'ring-1 ring-inset ring-line-strong',
                            )
                          : 'cursor-not-allowed text-ink-3/40',
                  )}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
